import React, { useState } from "react";
import { getApiBaseUrl } from "../utils/api";
// StravaAttribution import removed - Strava integration disabled from frontend (Feb 2026)
import { GarminAttribution } from "./GarminAttribution";
import "./ServiceConnector.css";
import { storage } from "../services/storage";
import { Platform } from "react-native";

// 'strava' removed from ServiceType - Strava integration disabled from frontend (Feb 2026)
export type ServiceType = 'garmin' | 'apple_health' | 'google_fit';
export type PlatformType = 'ios' | 'android' | 'desktop' | 'auto';

interface ServiceConnectorProps {
  service: ServiceType;
  platform: PlatformType;
  onConnect: (service: ServiceType) => Promise<void>;
  onCancel: () => void;
}

interface ServiceConfig {
  title: string;
  icon: string;
  color: string;
  description: string;
  buttonText: string;
}

const SERVICE_CONFIGS: Record<ServiceType, ServiceConfig> = {
  // Strava config removed - Strava integration disabled from frontend (Feb 2026)
  garmin: {
    title: 'Garmin Connect™',
    icon: '⌚',
    color: '#007DC3',
    description: "Share all the data you have in Garmin Connect™ with Coach Cedar for personalized coaching.",
    buttonText: 'Garmin Connect™'
  },
  apple_health: {
    title: 'Import Apple Health',
    icon: '❤️',
    color: '#007AFF',
    description: 'Export your Apple Health data for detailed insights into your daily activity and health metrics.',
    buttonText: 'Show Export Steps'
  },
  google_fit: {
    title: 'Import Google Fit',
    icon: '🤖',
    color: '#4285F4',
    description: 'Export your Google Fit data to understand your movement patterns and activity trends.',
    buttonText: 'Show Export Steps'
  }
};

export const ServiceConnector: React.FC<ServiceConnectorProps> = ({
  service,
  platform,
  onConnect,
  onCancel
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'intro' | 'connecting' | 'success' | 'instructions'>('intro');
  // stravaAuthUrl state removed - Strava integration disabled from frontend (Feb 2026)

  const config = SERVICE_CONFIGS[service];

  // Debug logging
  console.log('🔍 ServiceConnector Debug:', {
    service,
    platform,
    currentStep,
    isConnecting,
    error,
    config
  });

  const handleConnect = async () => {
    console.log('🔥 BUTTON CLICKED! handleConnect called for service:', service);
    setError(null);
    
    // Strava OAuth flow removed - Strava integration disabled from frontend (Feb 2026)
    // Backend code preserved in routers/strava_auth.py for future re-enablement
    
    if (service === 'garmin') {
      // 🚀 GARMIN OAUTH FLOW
      setCurrentStep('connecting');
      setIsConnecting(true);
      
      try {
        console.log('🔥 Starting Garmin OAuth flow...');
        
        const jwtToken = await storage.getItem("jwt_token") || await storage.getItem("token");
        if (!jwtToken) {
          throw new Error('Authentication required. Please refresh the page.');
        }
        
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/auth/garmin/auth-url`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to get Garmin authorization URL');
        }
        
        const { auth_url, state } = await response.json();
        
        if (!auth_url) {
          throw new Error('No authorization URL received from server');
        }
        
        console.log('🚀 GARMIN OAUTH: Got auth URL, redirecting...');
        
        // Store return URL so we can come back to chat after OAuth
        if (Platform.OS === "web") {
          await storage.setItem("garmin_return_url", window.location.pathname);
          await storage.setItem("garmin_connection_in_progress", "true");
          await storage.setItem("garmin_oauth_state", state);
          window.location.href = auth_url;
        } else {
          console.log("📱 Native Garmin auth flow will be handled in a native module.");
        }
        
      } catch (err) {
        console.error('❌ GARMIN OAUTH: Setup failed:', err);
        setError(err instanceof Error ? err.message : 'Connection failed. Please try again.');
        setCurrentStep('intro');
        setIsConnecting(false);
      }
    } else {
      // Show instructions for Apple Health and Google Fit
      setCurrentStep('instructions');
    }
  };

  const handleFileUpload = () => {
    const serviceNames: Record<ServiceType, string> = {
      apple_health: "Apple Health",
      google_fit: "Google Fit",
      // strava removed - Strava integration disabled from frontend (Feb 2026)
      garmin: "Garmin",
    };
    const serviceName = serviceNames[service] || service;

    if (Platform.OS === 'web') {
      // Web: Use window.location for navigation
      const params = new URLSearchParams({
        from: "settings",
        context: `${serviceName} data upload`,
        contextType: "data_upload",
        service,
      });
      window.location.href = `/chat?${params.toString()}`;
    } else {
      // Native: Use expo-router (dynamic import to avoid bundling for web)
      const { router } = require('expo-router');
      router.push({
        pathname: "/(tabs)/chat",
        params: {
          from: "settings",
          context: `${serviceName} data upload`,
          initialMessage: `I'm ready to upload my ${serviceName} data file. I've followed the export instructions and have the file ready to share.`,
          contextType: "data_upload",
          service,
          title: `${serviceName} Data Upload`,
          description: `User is ready to upload ${serviceName} data file`,
          action: "upload",
        },
      });
    }

    onCancel();
  };

  const renderInstructions = () => {
    if (service === 'apple_health') {
      return (
        <div className="instructions-container">
          <div className="disclaimer-section">
            <div className="disclaimer-badge">Research Beta</div>
            <p>
              <strong>Direct connectivity with Apple HealthKit is not yet supported</strong>, but you can 
              easily export your data and share it with Coach Cedar in the chat. Follow the instructions 
              below and when you're ready with your file, click the button to share it.
            </p>
          </div>
          
          <div className="section-title">How to Export Your Apple Health Data</div>
          
          <div className="instruction-step">
            <div className="step-number">1</div>
            <div className="step-text"><strong>Open the Health app</strong> on your iPhone</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">2</div>
            <div className="step-text"><strong>Tap your profile picture</strong> in the top right corner</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">3</div>
            <div className="step-text"><strong>Select "Export All Health Data"</strong></div>
          </div>
          <div className="instruction-step">
            <div className="step-number">4</div>
            <div className="step-text"><strong>Choose "Export"</strong> and wait for the file to be prepared</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">5</div>
            <div className="step-text"><strong>Save the file</strong> to your device or share it directly</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">6</div>
            <div className="step-text"><strong>Click the button below</strong> to share it with Coach Cedar</div>
          </div>
          
          <div className="action-buttons">
            <button 
              className="primary-button apple-health-btn"
              onClick={handleFileUpload}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <span className="spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="white"/>
                    <rect x="10.5" y="7" width="3" height="10" rx="1.5" fill="#1F6C4F"/>
                    <rect x="7" y="10.5" width="10" height="3" rx="1.5" fill="#1F6C4F"/>
                  </svg>
                  Share Apple Health data with Coach Cedar
                </>
              )}
            </button>
            <button 
              className="secondary-button"
              onClick={() => setCurrentStep('intro')}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    if (service === 'google_fit') {
      return (
        <div className="instructions-container">
          <div className="disclaimer-section">
            <div className="disclaimer-badge">Research Beta</div>
            <p>
              <strong>Direct connectivity with Google Fit API is not yet supported</strong>, but you can 
              easily export your data and share it with Coach Cedar in the chat. Follow the instructions 
              below and when you're ready with your file, click the button to share it.
            </p>
          </div>
          
          <div className="section-title">How to Export Your Google Fit Data</div>
          
          <div className="instruction-step">
            <div className="step-number">1</div>
            <div className="step-text">
              <strong>
                <a 
                  href="https://takeout.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  Open Google Takeout ↗
                </a>
              </strong> in your browser
            </div>
          </div>
          <div className="instruction-step">
            <div className="step-number">2</div>
            <div className="step-text"><strong>Find and check "Fit (Activity data)"</strong></div>
          </div>
          <div className="instruction-step">
            <div className="step-number">3</div>
            <div className="step-text"><strong>Select JSON format</strong> for best compatibility</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">4</div>
            <div className="step-text"><strong>Click "Create Export"</strong> and wait for processing</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">5</div>
            <div className="step-text"><strong>Download the ZIP file</strong> when ready</div>
          </div>
          <div className="instruction-step">
            <div className="step-number">6</div>
            <div className="step-text"><strong>Click the button below</strong> to share it with Coach Cedar</div>
          </div>
          
          <div className="action-buttons">
            <button 
              className="primary-button google-fit-btn"
              onClick={handleFileUpload}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <span className="spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                  </svg>
                  Share Google Fit data with Coach Cedar
                </>
              )}
            </button>
            <button 
              className="secondary-button"
              onClick={() => setCurrentStep('intro')}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div 
      className="service-connector"
      style={{ 
        '--service-color': config.color,
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 1000
      } as React.CSSProperties}
    >
      {/* Header */}
      <div className="connector-header">
        <span className="service-icon">{config.icon}</span>
        <div className="service-info">
          <h3>{config.title}</h3>
          <p>{config.description}</p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="error-message">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Content based on current step */}
      {currentStep === 'connecting' && (
        <div className="connecting-container">
          <div className="step-indicator">
            <div className="step active">1</div>
            <div className="step-line active"></div>
            <div className="step active">2</div>
            <div className="step-line"></div>
            <div className="step">3</div>
          </div>
          <h4>Redirecting to Garmin...</h4>
          <p>You'll be redirected to Garmin to authorize Coach Cedar, then brought back here automatically.</p>
          
          <div className="oauth-redirect-instructions">
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              textAlign: 'center',
              margin: '16px 0'
            }}>
              <div className="spinner large" style={{ marginBottom: '16px' }}></div>
              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Preparing Garmin authorization...</p>
              <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                You'll be redirected to Garmin in a moment. After authorizing, you'll return here automatically.
              </p>
            </div>
            {/* Service-specific Attribution - Strava removed (Feb 2026) */}
            {service === 'garmin' && <GarminAttribution theme="light" size="small" className="garmin-connector-attribution" />}
          </div>
        </div>
      )}

      {currentStep === 'success' && (
        <div className="success-container">
          <div className="step-indicator">
            <div className="step completed">✓</div>
            <div className="step-line completed"></div>
            <div className="step completed">✓</div>
            <div className="step-line completed"></div>
            <div className="step completed">✓</div>
          </div>
          <h4>Successfully Connected!</h4>
          <p>Your {service === 'garmin' ? 'Garmin' : service} account is now connected. I'll start analyzing your activities for personalized coaching.</p>
          <button 
            className="primary-button"
            onClick={onCancel}
            style={{ marginTop: '16px' }}
          >
            Continue
          </button>
        </div>
      )}

      {currentStep === 'intro' && (
        <>
          {(() => {
            console.log('🔍 Rendering intro step for service:', service, 'currentStep:', currentStep);
            return null;
          })()}
          {/* Strava connect button removed - Strava integration disabled from frontend (Feb 2026) */}
          
          {service === 'garmin' && (
            <div className="action-buttons">
              <button 
                className="primary-button garmin-connect-button"
                onClick={handleConnect}
                disabled={isConnecting}
                style={{ 
                  background: '#007DC3',
                  opacity: isConnecting ? 0.7 : 1,
                }}
              >
                {isConnecting ? (
                  <>
                    <span className="spinner"></span>
                    Connecting...
                  </>
                ) : (
                  <>
                    <img
                      src="/garmin-assets/Garmin Tag-white-high-res.png"
                      alt="Garmin Connect"
                      style={{
                        height: '22px',
                        width: 'auto',
                        display: 'block'
                      }}
                    />
                    Garmin Connect™
                  </>
                )}
              </button>
              <button 
                className="secondary-button"
                onClick={onCancel}
                disabled={isConnecting}
              >
                Not now
              </button>
            </div>
          )}
          
          {service !== 'garmin' && (
            <div className="action-buttons">
              <button 
                className="primary-button"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  config.buttonText
                )}
              </button>
              <button 
                className="secondary-button"
                onClick={onCancel}
                disabled={isConnecting}
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      {currentStep === 'instructions' && renderInstructions()}

      {/* Privacy note */}
      <div className="privacy-note">
        <span>🔒</span>
        <span>Your data is encrypted and only used for personalized coaching</span>
      </div>

      {/* Service Attribution - Strava removed (Feb 2026), Garmin only */}
      {service === 'garmin' && (
        <GarminAttribution theme="light" size="small" className="garmin-connector-attribution" />
      )}
    </div>
  );
};
